import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { getMentors } from "@/services/mentorService.ts";
import { getMentees, updateMenteeWithMentor } from "@/services/menteeService.ts";
import { fetchMaxNumberOfMentees } from "@/services/settingsService.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useToast } from "@/components/ui/use-toast.ts";
import { useTranslation } from "react-i18next";
import { Mentor } from "@/types/Mentor.ts";

type AssignMenteeButtonProps = {
    seasonId?: number;
};

export function AssignMenteeButton({ seasonId }: AssignMenteeButtonProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [assignOpen, setAssignOpen] = useState(false);
    const [selectedMenteeId, setSelectedMenteeId] = useState<string>("");
    const [selectedMentorId, setSelectedMentorId] = useState<string>("");
    const [searchMentee, setSearchMentee] = useState("");

    const { data: mentors = [] } = useQuery({
        queryKey: [...queryKeys.fetchMentors.all, seasonId ?? "active"],
        queryFn: () => getMentors("?include=mentorship-group", seasonId),
        initialData: [],
    });

    const { data: mentees = [] } = useQuery({
        queryKey: [...queryKeys.fetchMentees.all, seasonId ?? "active"],
        queryFn: () => getMentees(seasonId),
        initialData: [],
    });

    const { data: maxMentees = 0 } = useQuery({
        queryKey: queryKeys.fetchSettings.maxNumberOfMentees,
        queryFn: () => fetchMaxNumberOfMentees(),
        initialData: 0,
    });

    const assignMutation = useMutation({
        mutationFn: ({ menteeId, mentorId }: { menteeId: number; mentorId: number }) =>
            updateMenteeWithMentor(menteeId, { mentorId }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.fetchMentors.all });
            await queryClient.invalidateQueries({ queryKey: queryKeys.fetchSuggestions.mentees });
            await queryClient.invalidateQueries({ queryKey: queryKeys.fetchSuggestions.mentors });
        },
    });

    const mentorByMenteeId = new Map<number, Mentor>();
    mentors.forEach((mentor) => {
        mentor.mentees?.forEach((m) => mentorByMenteeId.set(m.id, mentor));
    });

    return (
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="border-primary shrink-0">
                    {t("dashboard.assignMentee")}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t("dashboard.assignMentee")}</DialogTitle>
                    <DialogDescription>{t("dashboard.assignMenteeDesc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{t("dashboard.searchMentee")}</label>
                        <Input
                            value={searchMentee}
                            onChange={(e) => setSearchMentee(e.target.value)}
                            placeholder={t("dashboard.searchMenteePlaceholder")}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{t("dashboard.selectMentee")}</label>
                        <select
                            className="w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm h-10 focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:border-accent/50"
                            value={selectedMenteeId}
                            onChange={(e) => setSelectedMenteeId(e.target.value)}
                        >
                            <option value="">{t("dashboard.selectMenteePlaceholder")}</option>
                            {mentees
                                .filter((m) => {
                                    const query = searchMentee.toLowerCase();
                                    return !query || m.user?.fullName?.toLowerCase().includes(query) || m.user?.email?.toLowerCase().includes(query);
                                })
                                .map((m) => {
                                    const currentMentor = mentorByMenteeId.get(m.id);
                                    return (
                                        <option key={m.id} value={String(m.id)}>
                                            {m.user?.fullName} ({m.user?.email}){currentMentor ? ` • ${t("dashboard.currentMentor")}: ${currentMentor.user.fullName}` : ""}
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{t("dashboard.selectMentor")}</label>
                        <select
                            className="w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm h-10 focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:border-accent/50"
                            value={selectedMentorId}
                            onChange={(e) => setSelectedMentorId(e.target.value)}
                        >
                            <option value="">{t("dashboard.selectMentorPlaceholder")}</option>
                            {mentors.map((m) => (
                                <option key={m.id} value={String(m.id)}>
                                    {m.user.fullName} ({m.mentees?.length ?? 0}/{maxMentees || "—"})
                                </option>
                            ))}
                        </select>
                        {selectedMentorId && (() => {
                            const mentor = mentors.find((m) => m.id === parseInt(selectedMentorId, 10));
                            if (!mentor) return null;
                            const count = mentor.mentees?.length ?? 0;
                            const limit = Number(maxMentees) || 0;
                            if (!limit) return null;
                            const over = count >= limit;
                            return (
                                <p className={`text-xs ${over ? "text-tangerine" : "text-muted-foreground"}`}>
                                    {over ? t("dashboard.capacityExceeded") : t("dashboard.capacityOk")} ({count}/{limit})
                                </p>
                            );
                        })()}
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setAssignOpen(false)}>{t("common.cancel")}</Button>
                    <Button
                        onClick={() => {
                            if (!selectedMenteeId || !selectedMentorId) return;
                            assignMutation.mutate(
                                { menteeId: parseInt(selectedMenteeId, 10), mentorId: parseInt(selectedMentorId, 10) },
                                {
                                    onSuccess: () => {
                                        setAssignOpen(false);
                                        setSelectedMenteeId("");
                                        setSelectedMentorId("");
                                        toast({ variant: "success", title: t("common.successTitle"), description: t("dashboard.menteeAssigned") });
                                    },
                                    onError: () => {
                                        toast({ variant: "destructive", title: t("common.errorTitle"), description: t("common.errorDesc") });
                                    },
                                }
                            );
                        }}
                        disabled={!selectedMenteeId || !selectedMentorId || assignMutation.isPending}
                    >
                        {t("dashboard.assignConfirm")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
